import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { clearInventoryAndSync } from '../../redux/inventorySlice';
import { trackEvent } from '../../services/analyticsService';
import { RootState, AppDispatch } from '../../redux/store';

export interface ClearPantryController {
  /** Whether the confirmation is showing. */
  confirming: boolean;
  /** True while the clear is in flight; the row shows "Clearing…". */
  busy: boolean;
  /** Open the confirmation. */
  ask(): void;
  /** Dismiss it without clearing. */
  cancel(): void;
  /** Confirmed — wipe the shelves. */
  confirm(): Promise<void>;
}

/**
 * The clear-pantry flow, shared by the mobile and desktop pantry screens.
 *
 * Both layouts show the same destructive row and the same confirmation, so the
 * dispatch, the busy state and the outcome copy live here rather than being
 * written twice and drifting.
 *
 * `flash` reports the outcome. The thunk empties local state even when the
 * cloud write fails, so there is always something to say: either the pantry is
 * gone everywhere, or it is gone here and may come back.
 */
export function useClearPantry(
  flash: (message: string) => void
): ClearPantryController {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const userId = useSelector((state: RootState) => state.auth?.user?.uid);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirm = useCallback(async () => {
    setConfirming(false);
    setBusy(true);
    try {
      await dispatch(clearInventoryAndSync({ userId })).unwrap();
      trackEvent('pantry_cleared', { outcome: 'synced' });
      flash(t('pantry.clear.done'));
    } catch {
      trackEvent('pantry_cleared', { outcome: 'local_only' });
      flash(t('pantry.clear.localOnly'));
    } finally {
      setBusy(false);
    }
  }, [dispatch, userId, flash, t]);

  return {
    confirming,
    busy,
    ask: useCallback(() => setConfirming(true), []),
    cancel: useCallback(() => setConfirming(false), []),
    confirm,
  };
}
